import { fetchFromRTM } from './index';
import * as constants from './constants';
import './typedef';

class ScenarioResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Creates a new scenario in the RTM. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-scenario-create/)
   * @param {string} parentId
   * @param {string} lifecycle The invocation time.  See linked docs.
   * @param {string} name
   * @param {Object} options optional fields to be spread into the message.  see docs.
   * @returns {Promise.<ScenarioDTO>}
   */
  async authorScenarioCreate(parentId, lifecycle, name, options) {
    const message = {
      type: constants.RTM_AUTHOR_SCENARIO_CREATE,
      parentId,
      lifecycle,
      name,
      ...options,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SCENARIO_CREATE_OK,
      constants.RTM_AUTHOR_SCENARIO_CREATE_ERROR
    );
    return response.scenario;
  }

  /**
   * Retrieves a list of scenarios for a parent and lifecycle. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-scenario-list/)
   * @param {string} parentId
   * @param {string} lifecycle
   * @returns {Promise.<[ScenarioDTO]>}
   */
  async authorScenarioList(parentId, lifecycle) {
    const message = {
      type: constants.RTM_AUTHOR_SCENARIO_LIST,
      parentId,
      lifecycle,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SCENARIO_LIST_OK,
      constants.RTM_AUTHOR_SCENARIO_LIST_ERROR
    );
    return response.scenarios;
  }

  /**
   * Replace an existing scenario. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-scenario-replace/)
   * @param {string} scenarioId
   * @param {string} name
   * @param {Object} options optional fields to be spread into the message.  see docs.
   * @returns {Promise.<ScenarioDTO>}
   */
  async authorScenarioReplace(scenarioId, name, options) {
    const message = {
      type: constants.RTM_AUTHOR_SCENARIO_REPLACE,
      scenarioId,
      name,
      ...options,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SCENARIO_REPLACE_OK,
      constants.RTM_AUTHOR_SCENARIO_REPLACE_ERROR
    );
    return response.scenario;
  }

  /**
   * See [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-scenarios-reorder-ok/)
   * @typedef {Object} ScenarioResponse
   * @property {string} ScenarioResponse.parentId
   * @property {string} ScenarioResponse.lifecycle
   * @property {[string]} ScenarioResponse.scenarioIds
   */

  /**
   * Re-orders the existing scenarios over the RTM. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-scenarios-reorder/)
   * @param {string} parentId
   * @param {string} lifecycle
   * @param {[string]} scenarioIds
   * @returns {Promise.<ScenarioResponse>}
   */
  async authorScenariosReorder(parentId, lifecycle, scenarioIds) {
    const message = {
      type: constants.RTM_AUTHOR_SCENARIOS_REORDER,
      parentId,
      lifecycle,
      scenarioIds,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SCENARIOS_REORDER_OK,
      constants.RTM_AUTHOR_SCENARIOS_REORDER_ERROR
    );
    return response.scenariosByParent;
  }
  /**
   *
   * @param {*} InteractiveId
   * @param {*} scope
   */
  async authorScenarioEvaluate(interactiveId, scopeData) {
    const message = {
      type: constants.RTM_AUTHOR_SCENARIOS_TEST,
      interactiveId,
      scopeData,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_SCENARIOS_TEST_OK,
      constants.RTM_AUTHOR_SCENARIOS_TEST_ERROR
    );
    return response;
  }
}

export default ScenarioResolver;
